/**
 * Audit Provenance Panel
 *
 * Shows users their calculation's audit trail:
 * - SHA-256 reproducibility hash
 * - Factor provenance (which factors, what values, what source)
 * - Engine version and calculation timestamp
 *
 * Principle: "transparent → verifiable"
 * This is what a verifier checks first.
 */
import React, { useState } from "react";
import { ShieldCheck, ChevronDown, Copy, Check } from "lucide-react";

export default function AuditProvenancePanel({ auditData }) {
  const [open,   setOpen]   = useState(false);
  const [copied, setCopied] = useState(false);

  if (!auditData?.calculation_hash) return null;

  const hash     = auditData.calculation_hash;
  const shortHash = hash.substring(0, 16) + "...";
  const factors   = auditData.factor_summary || [];

  const copyHash = async () => {
    try {
      await navigator.clipboard.writeText(hash);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="audit-panel">
      {/* Header — always visible */}
      <button
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
        onClick={() => setOpen(o => !o)}
        data-testid="audit-toggle-btn"
      >
        <div className="flex items-center gap-2">
          <ShieldCheck size={13} className="text-slate-500" />
          <p className="text-xs font-bold text-[#0F172A]">Calculation Audit Trail</p>
          <span className="text-[9px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full">
            {auditData.engine || "V1"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9px] text-slate-400 font-mono hidden sm:block">{shortHash}</span>
          <ChevronDown size={12} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {/* Expanded content */}
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100">
          {/* Hash */}
          <div className="mt-3">
            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wide mb-1">Reproducibility Hash (SHA-256)</p>
            <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
              <code className="text-[9px] text-slate-600 font-mono flex-1 break-all">{hash}</code>
              <button onClick={copyHash}
                className="flex-shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
                title="Copy hash">
                {copied ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} />}
              </button>
            </div>
            <p className="text-[8px] text-slate-400 italic mt-1">
              {auditData.reproducibility_note}
            </p>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-2">
            {[
              ["Engine",      auditData.engine || "V1"],
              ["Methodology", (auditData.methodology || "GHG Protocol").substring(0, 30)],
              ["Calculated",  auditData.calculated_at
                ? new Date(auditData.calculated_at).toLocaleString("en-GB", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" })
                : "—"],
              ["Version",     auditData.methodology_version || "GHG-V1"],
            ].map(([l, v]) => (
              <div key={l} className="bg-slate-50 rounded-lg px-2.5 py-2">
                <p className="text-[8px] text-slate-400 uppercase tracking-wide">{l}</p>
                <p className="text-[10px] font-semibold text-slate-700 mt-0.5 truncate">{v}</p>
              </div>
            ))}
          </div>

          {/* Factor provenance */}
          {factors.length > 0 && (
            <div>
              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wide mb-1.5">Factors Used</p>
              <div className="space-y-1">
                {factors.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-[9px]">
                    <span className={`flex-shrink-0 px-1.5 py-0.5 rounded-full font-bold text-[8px] ${
                      f.category === "fuel" ? "bg-orange-50 text-orange-600" :
                      f.category === "electricity" ? "bg-blue-50 text-blue-600" :
                      f.category === "transport" ? "bg-teal-50 text-teal-600" : "bg-slate-100 text-slate-500"
                    }`}>{f.category}</span>
                    <span className="flex-1 text-slate-600 truncate">{f.name}</span>
                    <span className="text-slate-400 font-mono">{f.value} {f.unit}</span>
                    <span className="text-slate-300 hidden sm:block">{f.source}</span>
                  </div>
                ))}
                {/* Grid + diesel fallback if no DB factors */}
                {factors.length === 0 && auditData.grid_factor?.value && (
                  <div className="flex items-center gap-2 text-[9px]">
                    <span className="px-1.5 py-0.5 rounded-full font-bold text-[8px] bg-blue-50 text-blue-600">electricity</span>
                    <span className="flex-1 text-slate-600">Grid — {auditData.grid_factor.source}</span>
                    <span className="text-slate-400 font-mono">{auditData.grid_factor.value} kgCO₂e/kWh</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <p className="text-[8px] text-slate-400 italic border-t border-slate-100 pt-2">
            This audit trail is stored server-side and linked to your calculation run.
            The hash verifies that the inputs and factors shown produced this exact result.
          </p>
          {/* Input snapshot reconstructability note */}
          {auditData.factor_library_ver && (
            <div className="bg-emerald-50 rounded-lg px-2.5 py-1.5 border border-emerald-100 mt-1">
              <p className="text-[8px] text-emerald-700 font-semibold">
                Input snapshot frozen at calculation time (factor library: {auditData.factor_library_ver})
              </p>
              <p className="text-[8px] text-emerald-600 italic mt-0.5">
                Full reconstruction is possible from this snapshot even after inputs are updated.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
